// src/oura/components/newscan/StepTrack.tsx
//
// The step path in the New scan top bar, drawn like NewClientDialog's track
// (the kit's segmented control): a grey track, the current step a raised
// white segment. Done steps show a green check and can be clicked to go
// back; a step that cannot be reached yet is grey text and takes no click.
// Under 640px the other steps fold to their numerals, done ones included
// (each keeps an aria-label with its name and state); the current step keeps its label beside its numeral and
// takes the room it needs, so the bar always names where you are.

import { CheckIcon } from "lucide-react";
import { cn } from "@oura/lib/cn";
import { FOCUS_RING, TYPE } from "@oura/ui";
import { FRAME } from "@oura/newscan/copy";

export interface StepTrackItem<K extends string> {
    key: K;
    label: string;
}

export interface StepTrackProps<K extends string> {
    steps: StepTrackItem<K>[];
    current: K;
    /** Whether a step other than the current one can be opened. */
    canReach(key: K): boolean;
    onGo(key: K): void;
    /**
     * A step before the current one that still needs a look (a First AI check's
     * Business before "Looks right"): no check, an amber dot instead.
     */
    needsLook?(key: K): boolean;
    className?: string;
}

/** The Seg raised-segment shadow (Controls.tsx), as NewClientDialog uses it. */
const RAISED = "bg-white text-gray-900 shadow-[0_1px_2px_rgba(0,0,0,0.08),0_0_0_1px_rgba(0,0,0,0.04)]";

export function StepTrack<K extends string>({ steps, current, canReach, onGo, needsLook, className }: StepTrackProps<K>) {
    const at = steps.findIndex((s) => s.key === current);
    return (
        <nav aria-label={FRAME.stepTrack} className={cn("min-w-0", className)}>
            <ol
                className={cn(
                    "list-plain m-0 flex gap-0.5 rounded-[10px] bg-gray-100 p-0.5 sm:grid",
                    steps.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-4",
                )}
            >
                {steps.map((s, i) => {
                    const now = i === at;
                    const look = !now && Boolean(needsLook?.(s.key));
                    const done = i < at && !look;
                    const open = !now && canReach(s.key);
                    const state = now ? FRAME.stepCurrent : look ? FRAME.stepNeedsLook : done ? FRAME.stepDone : open ? "" : FRAME.stepLocked;
                    const body = (
                        <>
                            {/* On a phone every other step is its number (a done one too); wider, a done step is a check. */}
                            {done && <CheckIcon aria-hidden size={14} strokeWidth={2.5} className="shrink-0 text-emerald-600 max-sm:hidden" />}
                            {look && <span aria-hidden className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />}
                            <span aria-hidden className="tabular-nums sm:hidden">
                                {i + 1}
                            </span>
                            <span className={now ? undefined : "max-sm:sr-only"}>{s.label}</span>
                            <span className="sr-only">
                                {`, step ${i + 1} of ${steps.length}`}
                                {state ? `, ${state}` : ""}
                            </span>
                        </>
                    );
                    // Each step named in full for a screen reader, whatever the phone shows of it.
                    const label = `${s.label}, step ${i + 1} of ${steps.length}${state ? `, ${state}` : ""}`;
                    const shape = cn(
                        TYPE.tc,
                        "flex h-8 w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 font-medium sm:px-3.5",
                        now ? RAISED : look ? "text-gray-700" : open || done ? "text-gray-500" : "text-gray-400",
                    );
                    return (
                        <li
                            key={s.key}
                            className={cn("flex min-w-0", now ? "max-sm:flex-[1_0_auto]" : "max-sm:flex-1")}
                            aria-current={now ? "step" : undefined}
                        >
                            {open ? (
                                <button
                                    type="button"
                                    aria-label={label}
                                    onClick={() => onGo(s.key)}
                                    className={cn(shape, "hover:bg-white/60 hover:text-gray-900 motion-safe:transition-colors", FOCUS_RING)}
                                >
                                    {body}
                                </button>
                            ) : (
                                <span className={shape} role="img" aria-label={label}>
                                    {body}
                                </span>
                            )}
                        </li>
                    );
                })}
            </ol>
        </nav>
    );
}
